import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Lock, Shield, Key, RefreshCw, Copy, Eye, EyeOff, Loader2, Trash2, AlertTriangle, Monitor, Smartphone, Globe } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const SecurityTab = ({ darkMode, token, subAdmins, fetchSubAdmins }) => {
  const [credForm, setCredForm] = useState({ current_password: "", new_email: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [deviceAlerts, setDeviceAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(true);

  const valText = darkMode ? "text-gray-100" : "text-gray-900";
  const lblText = darkMode ? "text-gray-400" : "text-gray-500";
  const cardBg = darkMode ? "bg-gray-800/80 border-gray-700/50" : "bg-white";
  const inputCls = darkMode ? "bg-gray-700 border-gray-600 text-gray-200 placeholder:text-gray-400" : "";
  const btnOutline = darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : "";

  const updateCredentials = async () => {
    if (!credForm.current_password) {
      toast.error("Current password is required");
      return;
    }
    if (!credForm.new_email && !credForm.new_password) {
      toast.error("Please provide a new email or password to update");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/security/update-credentials`, credForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Credentials updated successfully");
      setCredForm({ current_password: "", new_email: "", new_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update credentials");
    } finally {
      setSaving(false);
    }
  };

  const rotateCode = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/sub-admins/${id}/rotate-access-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`New access code: ${res.data.access_code}`);
      fetchSubAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rotate code");
    }
  };

  const revokeCode = async (id) => {
    if (!window.confirm("Revoke this access code? The sub-admin won't need a code to access the panel.")) return;
    try {
      await axios.post(`${API}/admin/sub-admins/${id}/revoke-access-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Access code revoked");
      fetchSubAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke code");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Access code copied to clipboard");
  };

  const toggleShowCode = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await axios.get(`${API}/admin/device-alerts`, { headers: { Authorization: `Bearer ${token}` } });
        setDeviceAlerts(res.data);
      } catch { }
      finally { setAlertsLoading(false); }
    };
    fetchAlerts();
  }, [token]);

  return (
    <>
      {/* Super Admin Credentials */}
      <Card className={`rounded-2xl border shadow-soft ${cardBg}`} data-testid="security-credentials-card">
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
            <Lock className={`w-5 h-5 ${darkMode ? "text-blue-400" : "text-primary"}`} /> Super Admin Credentials
          </CardTitle>
          <p className={`text-sm ${lblText}`}>Update your admin email and password</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label className={lblText}>Current Password <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={credForm.current_password}
                onChange={e => setCredForm(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Enter current password to authorize changes"
                className={`rounded-xl ${inputCls}`}
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={lblText}>New Email</Label>
              <Input
                type="email"
                value={credForm.new_email}
                onChange={e => setCredForm(prev => ({ ...prev, new_email: e.target.value }))}
                placeholder="Leave blank to keep current email"
                className={`rounded-xl ${inputCls}`}
                data-testid="new-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={lblText}>New Password</Label>
              <Input
                type="password"
                value={credForm.new_password}
                onChange={e => setCredForm(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Leave blank to keep current password"
                className={`rounded-xl ${inputCls}`}
                data-testid="new-password-input"
              />
            </div>
            <Button className="rounded-xl" onClick={updateCredentials} disabled={saving} data-testid="update-credentials-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Update Credentials
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sub-Admin Access Codes */}
      <Card className={`rounded-2xl border shadow-soft mt-6 ${cardBg}`} data-testid="access-codes-card">
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
            <Key className={`w-5 h-5 ${darkMode ? "text-amber-400" : "text-amber-600"}`} /> Sub-Admin Access Codes
          </CardTitle>
          <p className={`text-sm ${lblText}`}>Manage access codes for sub-admin authentication. Codes are required on first login and after 7 days of inactivity.</p>
        </CardHeader>
        <CardContent>
          {subAdmins.length === 0 ? (
            <p className={`text-sm ${lblText} text-center py-8`}>No sub-admins yet. Invite one from the Sub-Admins tab.</p>
          ) : (
            <div className="space-y-3">
              {subAdmins.map(sa => (
                <div key={sa.id} className={`flex items-center justify-between rounded-xl p-4 border ${darkMode ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`} data-testid={`access-code-row-${sa.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm truncate ${valText}`}>{sa.name || sa.email}</p>
                    <p className={`text-xs truncate ${lblText}`}>{sa.email}</p>
                    {sa.access_code_verified_at ? (
                      <Badge className={`mt-1 text-xs rounded-full ${darkMode ? "bg-green-900/30 text-green-300" : "bg-green-100 text-green-700"}`}>Verified</Badge>
                    ) : sa.access_code ? (
                      <Badge className={`mt-1 text-xs rounded-full ${darkMode ? "bg-yellow-900/30 text-yellow-300" : "bg-yellow-100 text-yellow-700"}`}>Pending verification</Badge>
                    ) : (
                      <Badge className={`mt-1 text-xs rounded-full ${darkMode ? "bg-gray-600 text-gray-300" : "bg-gray-200 text-gray-600"}`}>No code set</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {sa.access_code && (
                      <div className="flex items-center gap-1">
                        <code className={`text-sm font-mono px-2 py-1 rounded-lg ${darkMode ? "bg-gray-600 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
                          {showPasswords[sa.id] ? sa.access_code : "••••••••"}
                        </code>
                        <Button variant="outline" size="icon" className={`rounded-lg h-8 w-8 ${btnOutline}`} onClick={() => toggleShowCode(sa.id)} data-testid={`toggle-code-visibility-${sa.id}`}>
                          {showPasswords[sa.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </Button>
                        <Button variant="outline" size="icon" className={`rounded-lg h-8 w-8 ${btnOutline}`} onClick={() => copyCode(sa.access_code)} data-testid={`copy-code-${sa.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                    <Button variant="outline" size="sm" className={`rounded-lg ${btnOutline}`} onClick={() => rotateCode(sa.id)} data-testid={`rotate-code-${sa.id}`}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> {sa.access_code ? "Rotate" : "Generate"}
                    </Button>
                    {sa.access_code && (
                      <Button variant="outline" size="sm" className={`rounded-lg ${darkMode ? "text-red-400 hover:text-red-300 border-gray-600 hover:bg-red-900/20" : "text-destructive hover:bg-red-50"}`} onClick={() => revokeCode(sa.id)} data-testid={`revoke-code-${sa.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={`mt-4 rounded-xl p-3 text-sm ${darkMode ? "bg-blue-900/20 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            <strong>How it works:</strong> Sub-admins must enter their access code on first login. After 7 consecutive days of inactivity, they'll be prompted to re-enter it.
          </div>
        </CardContent>
      </Card>

      {/* Multi-Device Security Alerts */}
      <Card className={`rounded-2xl border shadow-soft mt-6 ${cardBg}`} data-testid="device-alerts-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
                <Monitor className={`w-5 h-5 ${darkMode ? "text-red-400" : "text-red-600"}`} /> Multi-Device Security Alerts
              </CardTitle>
              <p className={`text-sm mt-1 ${lblText}`}>Users with 5+ devices trigger automatic verification emails</p>
            </div>
            {deviceAlerts && (
              <div className="flex items-center gap-3">
                <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                  {deviceAlerts.total_devices} total devices tracked
                </div>
                {deviceAlerts.total_flagged > 0 && (
                  <Badge className="bg-red-500 text-white border-0">{deviceAlerts.total_flagged} flagged</Badge>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {alertsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : !deviceAlerts || deviceAlerts.alerts.length === 0 ? (
            <div className={`text-center py-8 rounded-xl ${darkMode ? "bg-gray-700/30" : "bg-gray-50"}`}>
              <Shield className={`w-10 h-10 mx-auto mb-3 ${darkMode ? "text-green-400" : "text-green-500"}`} />
              <p className={`text-sm font-medium ${valText}`}>No multi-device alerts</p>
              <p className={`text-xs mt-1 ${lblText}`}>All user accounts are within normal device limits</p>
            </div>
          ) : (
            <div className="space-y-3">
              {deviceAlerts.alerts.map(alert => (
                <div key={alert.user_id} className={`rounded-xl border p-4 ${darkMode ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`} data-testid={`device-alert-${alert.user_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className={`font-medium text-sm ${valText}`}>{alert.name || "Unknown User"}</p>
                      <p className={`text-xs ${lblText}`}>{alert.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${alert.unverified_devices > 0 ? "bg-red-100 text-red-700" : darkMode ? "bg-amber-900/30 text-amber-300" : "bg-amber-100 text-amber-700"} border-0 text-xs`}>
                        {alert.total_devices} devices
                      </Badge>
                      {alert.unverified_devices > 0 && (
                        <Badge className="bg-red-500 text-white border-0 text-xs">{alert.unverified_devices} unverified</Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {alert.devices.map(device => (
                      <div key={device.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                        device.is_primary
                          ? (darkMode ? "bg-blue-900/30 border border-blue-700/40" : "bg-blue-50 border border-blue-200")
                          : !device.is_verified
                            ? (darkMode ? "bg-red-900/30 border border-red-700/40" : "bg-red-50 border border-red-200")
                            : (darkMode ? "bg-gray-600/50" : "bg-white border border-gray-200")
                      }`}>
                        <Smartphone className={`w-3.5 h-3.5 shrink-0 ${
                          device.is_primary ? "text-blue-500" : !device.is_verified ? "text-red-500" : (darkMode ? "text-gray-400" : "text-gray-500")
                        }`} />
                        <div className="min-w-0 flex-1">
                          <p className={`truncate font-medium ${valText}`}>{device.browser || "Unknown"}</p>
                          <p className={`truncate ${lblText}`}>{device.os} · {device.screen_size}</p>
                        </div>
                        {device.is_primary && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold shrink-0 ${darkMode ? "bg-blue-800 text-blue-200" : "bg-blue-100 text-blue-600"}`}>Primary</span>
                        )}
                        {!device.is_verified && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md font-semibold bg-red-500 text-white shrink-0">Blocked</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={`mt-4 rounded-xl p-3 text-sm ${darkMode ? "bg-amber-900/20 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
            <Shield className="w-4 h-4 inline mr-2" />
            <strong>How it works:</strong> When a user's account is accessed from a 5th unique device, the login is blocked and a verification email is sent to the user. The email contains a secure link valid for 1 hour.
          </div>
        </CardContent>
      </Card>
    </>
  );
};
